"use client";
import { Product } from "@/utils/types/product";
import {
  useState,
  useRef,
  useEffect,
  useAppSelector,
} from "@/utils/hooks/reactUseCustomHooks";
import { Box } from "@dxp-feed/ui/Box";
import { Button } from "@dxp-feed/ui/Button";
import {
  analyticsAddToFavouriteEventAfterSearch,
  analyticsAddToFavouriteEventBeforeSearch,
} from "@/services/algolia/analytics";
import { algoliaConfig } from "@/services/algolia/config";
import useInteractionEventAnalytics from "@/utils/hooks/useInteractionEventAnalytics";

interface Props {
  className?: string;
  textClassName?: string;
  onFavoriteToggle?: (isFavorite?: boolean, itemData?: Product) => void;
  itemDetail?: Product;
  isUserLoggedOut?: boolean;
  favouriteStatus?: boolean;
  unFavourite?: string;
  disabled?: boolean;
}
const FavoriteButton: React.FC<Props> = ({
  className = "",
  textClassName = "",
  onFavoriteToggle,
  itemDetail,
  isUserLoggedOut,
  favouriteStatus,
  unFavourite,
  disabled,
}) => {
  const [isFavorite, setIsFavorite] = useState<boolean>(false);
  const [buttonWidth, setButtonWidth] = useState<number>(40);
  const textRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isFavoriteStatus, setIsFavoriteStatus] = useState(favouriteStatus);

  const { productDetail }: any = useAppSelector(
    (state) => state?.getProductDetailsDataSlice
  );

  const heartButtonIcon =
    isFavoriteStatus && unFavourite == "" ? "icon-filled-heart" : "icon-heart";
  const heartButtonText =
    isFavoriteStatus && unFavourite == ""
    ? productDetail?.items?.[0]?.removeFromFavouriteCta ||  "Remove favourite"
     : productDetail?.items?.[0]?.addToFavouriteCta ||  "Add to favourite"

  const handleEvent = useInteractionEventAnalytics();

  const handleAddToFavouriteAlgoliaEvent = () => {
    handleEvent({
      id: String(itemDetail?.objectID),
      type: "products",
      eventType: "addToFavourite",
    });
  };

  const toggleFavorite = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    e.preventDefault();
    if (isUserLoggedOut) {
      if (onFavoriteToggle && itemDetail) {
        onFavoriteToggle(false, itemDetail);
      }
    } else {
      setIsFavorite((prev) => {
        const newFavoriteState = !prev;
        if (onFavoriteToggle && itemDetail) {
          onFavoriteToggle(newFavoriteState, itemDetail);
        }
        if (newFavoriteState && itemDetail) {
          handleAddToFavouriteAlgoliaEvent();
        }
        return newFavoriteState;
      });
      setIsFavoriteStatus((prev) => {
        const newFavoriteState = !prev;
        favouriteStatus = !prev;
        setIsFavoriteStatus(newFavoriteState);
        return newFavoriteState;
      });
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setButtonWidth(40);
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleBlur = () => {
    setIsHovered(false);
    setButtonWidth(40);
  };

  const handleFocus = () => {
    setIsHovered(true); // Ensures the button expands when focused
  };

  useEffect(() => {
    setIsFavoriteStatus(favouriteStatus);
  }, [favouriteStatus]);

  useEffect(() => {
    const textWidth = textRef.current ? textRef.current.offsetWidth : 0;
    setButtonWidth(isFavorite ? 40 + textWidth + 14 : 40);
  }, [isFavorite]);

  useEffect(() => {
    if (isHovered && !disabled) {
      const textWidth = textRef.current ? textRef.current.offsetWidth : 0;
      setButtonWidth(40 + textWidth + 10);
    } else if (!isFavorite) {
      setButtonWidth(40);
    }
  }, [isHovered, isFavorite, disabled]);

  return (
    <Button
      onClick={toggleFavorite}
      aria-label={heartButtonText}
      className={`${className} ${disabled ? "cursor-not-allowed opacity-60" : ""} group !px-0 z-1 !bg-brandWhite flex items-center border rounded-full overflow-hidden transition-all duration-700 relative [&:before]:hidden text-brandSecondary max-lg:max-w-[40px] min-w-10 min-h-10`}
      type="button"
      variant="primary"
      style={{ width: `${buttonWidth}px`, height: "40px" }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onBlur={handleBlur}
      onFocus={handleFocus}
      id="favorite_button"
    >
      <Box
        as="div"
        className="relative overflow-hidden w-full h-full flex items-center max-lg:hidden"
      >
        <div
          ref={textRef}
          className={`${textClassName}  absolute left-2 transition-all !duration-1000 !delay-100 whitespace-nowrap text-[14px] ${
            isFavorite || isHovered
              ? "translate-x-0 opacity-100"
              : "translate-x-full"
          } group-hover:translate-x-0 group-hover:opacity-100`}
        >
          {heartButtonText}
        </div>
      </Box>
      <Box
        as="span"
        className={`${
          heartButtonIcon
        } min-w-10 w-10 h-10 bg-brandWhite text-xl flex items-center justify-center`}
      ></Box>
    </Button>
  );
};

export default FavoriteButton;
